import { useMutation, useQuery } from '@apollo/client';
import { MoreHorizontal, Flag, Ban, LogOut, Loader2, UserCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  Button,
} from '@artstorefronts/ui';
import { toast } from 'sonner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BLOCK_USER_MUTATION, CONNECTION_STATES_QUERY, UNBLOCK_USER_MUTATION } from '../../graphql/connection';
import { LEAVE_MESSAGE_MUTATION } from '../../graphql/direct-messages';
import { removeInboxThreadFromCache } from '@arthelper/shared/inbox-messages-cache';
import { BlockMemberDialog } from '../network/BlockMemberDialog';
import { ReportUserDialog } from '../network/ReportUserDialog';

export interface ThreadParticipant {
  userId: string;
  displayName: string;
}

interface ThreadActionsMenuProps {
  /** The message/thread ID used for the leave action. */
  messageId: string;
  /** Single participant for 1:1 threads. */
  otherUserId?: string;
  /** All other participants for group threads (>1 entry triggers per-participant submenus). */
  participants?: ThreadParticipant[];
  onBlock?: (userId: string) => void;
  onLeave?: () => void;
}

export function ThreadActionsMenu({ messageId, otherUserId, participants, onBlock, onLeave }: ThreadActionsMenuProps) {
  const { t } = useTranslation('social');
  const [blockUser, { loading: blocking }] = useMutation(BLOCK_USER_MUTATION);
  const [unblockUser, { loading: unblocking }] = useMutation(UNBLOCK_USER_MUTATION);
  const [leaveMessage, { loading: leaving }] = useMutation(LEAVE_MESSAGE_MUTATION, {
    update(cache, { data }) {
      if ((data as { leaveMessage?: boolean | null } | null | undefined)?.leaveMessage !== true) return;
      // Page-0 refetches keep rows past the page, so a left thread that sits in
      // that tail survives the merge. Pull it out of every inbox list here.
      removeInboxThreadFromCache(cache, messageId);
    },
  });
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [reportConfirmUserId, setReportConfirmUserId] = useState<string | null>(null);
  // A block is invisible to the person it lands on and an unblock restores
  // almost nothing, so neither fires straight off the menu.
  const [confirmUserId, setConfirmUserId] = useState<string | null>(null);

  // Normalise: if a legacy `otherUserId` is passed without `participants`, treat
  // it as a single-participant list so the rest of the logic is uniform.
  let resolvedParticipants: ThreadParticipant[] = [];
  if (participants && participants.length > 0) {
    resolvedParticipants = participants;
  } else if (otherUserId) {
    resolvedParticipants = [{ userId: otherUserId, displayName: t('block.this-member', 'this member') }];
  }

  const isGroup = resolvedParticipants.length > 1;
  const userIds = resolvedParticipants.map((p) => p.userId);
  const connectionStatesVariables = { userIds };

  // The SPA's one connectionStates document, keyed on this thread's
  // participants — the only thing that decides Block versus Unblock here.
  const { data: connectionStatesData } = useQuery(CONNECTION_STATES_QUERY, {
    variables: connectionStatesVariables,
    skip: userIds.length === 0,
  });

  const blockedIds = new Set<string>(
    ((connectionStatesData?.connectionStates ?? []) as { userId: string; isBlockedByViewer?: boolean }[])
      .filter((state) => state.isBlockedByViewer)
      .map((state) => String(state.userId))
  );

  // Refetching that same document is what flips this menu and the profile's
  // Connect button together, with no parent prop threading.
  const refetchConnectionStates = [{ query: CONNECTION_STATES_QUERY, variables: connectionStatesVariables }];

  async function runBlock(userId: string) {
    try {
      await blockUser({
        variables: { userId, source: 'DM_THREAD' },
        refetchQueries: refetchConnectionStates,
      });
      setConfirmUserId(null);
      toast.success(t('block.toast-blocked', 'Blocked. They can no longer reach you on ArtHelper.'));
      onBlock?.(userId);
    } catch (error) {
      console.error('Failed to block user:', error);
      toast.error(t('block.toast-block-failed', "Couldn't block. Try again in a moment."));
    }
  }

  async function runUnblock(userId: string) {
    try {
      await unblockUser({ variables: { userId }, refetchQueries: refetchConnectionStates });
      setConfirmUserId(null);
      toast.success(t('block.toast-unblocked', 'Unblocked.'));
    } catch (error) {
      console.error('Failed to unblock user:', error);
      toast.error(t('block.toast-unblock-failed', "Couldn't unblock. Try again in a moment."));
    }
  }

  function handleConfirm() {
    if (!confirmUserId) return;
    if (blockedIds.has(confirmUserId)) {
      void runUnblock(confirmUserId);
      return;
    }
    void runBlock(confirmUserId);
  }

  async function handleLeave() {
    try {
      await leaveMessage({ variables: { messageId } });
      toast.success('You have left the conversation.');
      setLeaveConfirmOpen(false);
      onLeave?.();
    } catch (error) {
      console.error('Failed to leave conversation:', error);
      toast.error('Failed to leave conversation', { description: 'Please try again.' });
    }
  }

  if (resolvedParticipants.length === 0) return null;

  const confirmName =
    resolvedParticipants.find((p) => p.userId === confirmUserId)?.displayName ?? t('block.this-member', 'this member');
  const confirmIsUnblock = !!confirmUserId && blockedIds.has(confirmUserId);
  const blockPending = blocking || unblocking;

  function blockItem(participant: ThreadParticipant, named: boolean) {
    if (blockedIds.has(participant.userId)) {
      return (
        <DropdownMenuItem onClick={() => setConfirmUserId(participant.userId)} disabled={blockPending}>
          <UserCheck className="size-4 mr-3" />
          {t('block.menu-unblock', 'Unblock member')}
        </DropdownMenuItem>
      );
    }
    return (
      <DropdownMenuItem
        onClick={() => setConfirmUserId(participant.userId)}
        disabled={blockPending}
        className="text-red-600 focus:text-red-600"
      >
        <Ban className="size-4 mr-3" />
        {named
          ? t('block.menu-block-named', 'Block {{name}}', { name: participant.displayName })
          : t('block.menu-block', 'Block member')}
      </DropdownMenuItem>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1 rounded hover:bg-gray-100 focus:outline-none" aria-label="Thread actions">
            <MoreHorizontal className="w-5 h-5 text-gray-600" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={() => setLeaveConfirmOpen(true)} className="text-red-600 focus:text-red-600">
            <LogOut className="size-4 mr-3" />
            Leave conversation
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {isGroup ? (
            // Group thread: per-participant submenus
            resolvedParticipants.map((p) => (
              <DropdownMenuSub key={p.userId}>
                <DropdownMenuSubTrigger>
                  <span className="truncate max-w-[160px]">{p.displayName}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => setReportConfirmUserId(p.userId)}>
                    <Flag className="size-4 mr-3" />
                    Report {p.displayName}
                  </DropdownMenuItem>
                  {blockItem(p, true)}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ))
          ) : (
            // 1:1 thread: flat items
            <>
              <DropdownMenuItem onClick={() => setReportConfirmUserId(resolvedParticipants[0].userId)}>
                <Flag className="size-4 mr-3" />
                Report user
              </DropdownMenuItem>
              {blockItem(resolvedParticipants[0], false)}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* A sibling of the menu: the dropdown unmounts its content when an item
          is chosen, and a dialog rendered inside would go with it. */}
      <BlockMemberDialog
        open={confirmUserId !== null}
        mode={confirmIsUnblock ? 'unblock' : 'block'}
        name={confirmName}
        pending={blockPending}
        onCancel={() => setConfirmUserId(null)}
        onConfirm={handleConfirm}
      />

      <AlertDialog open={leaveConfirmOpen} onOpenChange={setLeaveConfirmOpen}>
        <AlertDialogContent className="sm:max-w-[425px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              You will no longer see this conversation. You can be re-added by other participants.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setLeaveConfirmOpen(false)} disabled={leaving}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLeave} disabled={leaving}>
              {leaving && <Loader2 className="size-4 mr-2 animate-spin" />}
              Leave
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {reportConfirmUserId && (
        <ReportUserDialog
          open={!!reportConfirmUserId}
          contentType="user"
          contentId={reportConfirmUserId}
          title={`Report ${resolvedParticipants.find((p) => p.userId === reportConfirmUserId)?.displayName ?? 'User'}`}
          onClose={() => setReportConfirmUserId(null)}
          onSuccess={() => setReportConfirmUserId(null)}
        />
      )}
    </>
  );
}
